import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { AssetCategoryResponse, AssetCategoryCreateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper } from '../utils';
import { ICONS } from '../icons';
import { AssetCategoryDetailsSectionHandler } from './details.section';
import { ContactsSectionHandler } from '../shared/contacts.section';
import { AssetGroupingService } from 'app/services';
import { AssetListSectionHandler } from '../shared/list.section';
import { RegisterEntityHandler } from '../../entities';

@RegisterEntityHandler('assetcategory')
@Injectable()
export class AssetCategoryHandler extends EntityHandler<AssetCategoryResponse> {

    currentEntity: AssetCategoryResponse;

    constructor(
        admin: AdminEditorService,
        private grouping: AssetGroupingService
    ) {
        super(admin);
        this.listMode = 'tree';
    }

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        // we can't paginate tree items, so hard code a limit of 250 and hope it's enough
        offset = 0;
        limit = 250;
        return this.admin.getCached(`listAssetCategories/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listAssetCategories(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'assetcategory',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.ASSETCATEGORIES'),
        }));
    }

    getEntity(id: string): Promise<AssetCategoryResponse> {
        return this.admin.getCached(`getAssetCategory/${id}`, api => api.entities.getAssetCategory(id), 10).then(result => {
            this.currentEntity = result;
            return result;
        });
    }

    createEntity(ownerId: string, entity: AssetCategoryCreateRequest): Promise<AssetCategoryResponse> {
        entity.ownerId = ownerId;
        if (entity.parent.id === 'root') {
            entity.parent.id = '00000000-0000-0000-0000-000000000000';
        }
        this.grouping.clear(); // clear our grouping cache after an update
        return this.admin.applyUpdate(api => api.entities.createAssetCategory(entity));
    }

    setEntityState(entity: AssetCategoryResponse, state: string): Promise<AssetCategoryResponse> {
        if (state === 'deleted') {
            this.grouping.clear(); // clear our grouping cache after an update
            this.currentEntity = null;
            return this.admin.applyUpdate(api => api.entities.deleteAssetCategory(entity.id));
        }
        return Promise.reject('Only deletion supported');
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSETCATEGORY.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', (row) => {
                return row.folder ? ICONS.folder : ICONS.assetcategory;
            }),
            // grid.TextColumn('assets', 'ASSETS', 80), TODO: add back when the backend can return a count
            grid.TimeAgoColumn('entity.modifiedDate', 'MODIFIED'),
        ];
        return Promise.resolve(columns);
    }

    getEditorSections(ownerId: string, entity: AssetCategoryResponse): Promise<SectionHandlerGroup<AssetCategoryResponse>[]> {

        return this.grouping.getAssetCategories(ownerId).then(assetCategories => {

            const index = (entity && entity.index) || 0;
            const root = assetCategories.find(x => x.index === index && x.parent === 'root');
            const rootName = root && root.name;

            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSET.COLUMNS');
            const { id, name, parent } = this.currentEntity;
            return [{
                name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
                sections: [
                    new AssetCategoryDetailsSectionHandler(this.admin, ownerId, this.grouping, false),
                    new ContactsSectionHandler(this.admin, ownerId, 'assetcategory'),
                ],
            }, {
                name: this.admin.i18n.instant('ADMIN.EDITOR.ASSETS'),
                sections: [
                    new AssetListSectionHandler(this.admin, ownerId, parent ? ['add', 'delete'] : ['delete'], this.grouping, 'categories', [
                        {
                            ...grid.IdNameComboColumn('categories', rootName, [], 150),
                            format: (col, row) => {
                                return row[col.field][index] && row[col.field][index].name;
                            },
                        },
                    ], { id, name }, null, async (action, update) => {
                        update[index] = action === 'add' ? { id, name } : null;
                        return update;
                    }),
                ],
            }];
        });
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<AssetCategoryResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'assetcategory',
            entity: {
                parent: this.currentEntity ? { id: this.currentEntity.id, name: this.currentEntity.name } : null,
            },
            sections: [
                new AssetCategoryDetailsSectionHandler(this.admin, ownerId, this.grouping, true),
            ],
        });
    }

}













